import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, ArrowLeft } from "lucide-react";

const ForgotPassword = () => {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const sendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      toast.success("If an account exists, a reset code was sent to that email");
      setStep(2);
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  const resetPw = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        new_password: pw,
      });
      toast.success("Password updated. Please sign in with your new password.");
      nav("/login");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto py-16 px-4" data-testid="forgot-password-page">
      <div className="text-center mb-6">
        <div className="w-14 h-14 rounded-full bg-[#EBF2EE] text-[#2D5A47] flex items-center justify-center mx-auto mb-3">
          <KeyRound size={24} />
        </div>
        <h1 className="font-serif text-4xl">Reset your password</h1>
        <p className="text-sm text-[#64748B] mt-2">
          {step === 1
            ? "Enter your email and we'll send you a 6-digit reset code."
            : "Enter the code we sent to your email, then choose a new password."}
        </p>
      </div>
      {step === 1 ? (
        <form onSubmit={sendCode} className="bg-white border border-[#E2DCD2] rounded-xl p-6 space-y-4">
          <div>
            <label className="text-sm font-medium">Email</label>
            <input
              data-testid="forgot-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full border border-[#E2DCD2] rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#2D5A47]"
              placeholder="you@example.com"
            />
          </div>
          <button type="submit" disabled={loading} className="w-full bg-[#2D5A47] hover:bg-[#214435] disabled:opacity-50 text-white py-3 rounded-lg font-semibold" data-testid="send-code-btn">
            {loading ? "Sending…" : "Send reset code"}
          </button>
          <div className="text-center text-sm">
            <Link to="/login" className="text-[#C86D42] hover:underline font-medium inline-flex items-center gap-1">
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </div>
        </form>
      ) : (
        <form onSubmit={resetPw} className="bg-white border border-[#E2DCD2] rounded-xl p-6 space-y-4">
          <div>
            <label className="text-xs text-[#64748B]">Email</label>
            <div className="mt-1 border border-[#E2DCD2] rounded-lg px-3 py-2 text-sm bg-[#F4F1EA] text-[#64748B]">{email}</div>
          </div>
          <div>
            <label className="text-sm font-medium">6-digit code</label>
            <input
              data-testid="reset-code"
              inputMode="numeric"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="123456"
              className="mt-1 w-full border border-[#E2DCD2] rounded-lg px-3 py-3 text-center font-mono text-xl tracking-[8px] outline-none focus:ring-2 focus:ring-[#2D5A47]"
            />
          </div>
          <div>
            <label className="text-sm font-medium">New password</label>
            <input
              data-testid="reset-password"
              type="password"
              required
              minLength={8}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="mt-1 w-full border border-[#E2DCD2] rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#2D5A47]"
              placeholder="At least 8 characters"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Confirm new password</label>
            <input
              data-testid="reset-password-confirm"
              type="password"
              required
              minLength={8}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="mt-1 w-full border border-[#E2DCD2] rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#2D5A47]"
            />
          </div>
          <button type="submit" disabled={loading} className="w-full bg-[#2D5A47] hover:bg-[#214435] disabled:opacity-50 text-white py-3 rounded-lg font-semibold" data-testid="reset-submit">
            {loading ? "Updating…" : "Update password"}
          </button>
          <div className="flex justify-between text-xs">
            <button type="button" onClick={() => setStep(1)} className="text-[#64748B] hover:text-[#1E2A24] inline-flex items-center gap-1">
              <ArrowLeft size={12} /> Use a different email
            </button>
            <button type="button" onClick={sendCode} disabled={loading} className="text-[#C86D42] font-semibold" data-testid="resend-code-btn">
              Resend code
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ForgotPassword;
